import { ModalBackdrop } from './ModalFrame';
import { ModalCloseButton } from './ModalCloseButton';
import { useEffect, useState } from 'react';
import { PasskeyManagementDialog } from '../auth/PasskeyManagementDialog';
import type { SessionViewerOrganizationRole, SessionViewerResult } from '../auth/api';
import '../styles/modules-my-account-security.css';
import { EmailChangePanel } from './EmailChangePanel';
import { MyCurrentSession } from './MyCurrentSession';
import { PasswordChangePanel } from './PasswordChangePanel';
import { PersonalSecurityJournal } from './PersonalSecurityJournal';
import { PrototypeIcon } from './PrototypeIcon';

interface MyAccountDialogProps {
  readonly viewer: SessionViewerResult;
  readonly onClose: () => void;
}

function roleLabel(role: SessionViewerOrganizationRole | undefined): string {
  switch (role) {
    case 'ORGANIZATION_ADMIN':
      return 'Administrateur';
    case 'PROVIDER':
      return 'Prestataire';
    case 'CLIENT':
      return 'Client';
    default:
      return 'Superviseur de plateforme';
  }
}

export function MyAccountDialog({ viewer, onClose }: MyAccountDialogProps): React.JSX.Element {
  const [passkeyManagementOpen, setPasskeyManagementOpen] = useState(false);
  const [emailChangeOpen, setEmailChangeOpen] = useState(false);
  const [passwordChangeOpen, setPasswordChangeOpen] = useState(false);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape' || passkeyManagementOpen) return;
      event.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose, passkeyManagementOpen]);

  if (passkeyManagementOpen) {
    return (
      <PasskeyManagementDialog
        csrfToken=""
        onClose={() => setPasskeyManagementOpen(false)}
        onReauthenticate={() => window.location.replace('/')}
      />
    );
  }

  return (
    <ModalBackdrop
      className="tosho-modal-backdrop my-account-backdrop"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
      aria-labelledby="my-account-title"
      close={
        <ModalCloseButton
          className="tosho-modal-close my-account-dialog__close"
          onClick={onClose}
          aria-label="Fermer Mon compte"
          title="Fermer"
        />
      }
    >
      <section className="tosho-modal-panel my-account-dialog">
        <header className="my-account-dialog__header tosho-modal-header">
          <div>
            <p>Compte personnel</p>
            <h2 id="my-account-title">Mon compte</h2>
          </div>
        </header>
        <div className="my-account-dialog__body">
          <section className="my-account-section" aria-labelledby="my-account-identity-title">
            <div className="my-account-section__heading">
              <p>Identité</p>
              <h3 id="my-account-identity-title">Informations du compte</h3>
            </div>
            <dl className="my-account-identity-grid">
              <div>
                <dt>Nom</dt>
                <dd>
                  {viewer.givenName} {viewer.familyName}
                </dd>
              </div>
              <div>
                <dt>Profil</dt>
                <dd>{roleLabel(viewer.organization?.role)}</dd>
              </div>
              {viewer.organization ? (
                <div>
                  <dt>Organisation</dt>
                  <dd>{viewer.organization.displayName}</dd>
                </div>
              ) : null}
            </dl>
          </section>

          <section className="my-account-section" aria-labelledby="my-account-security-title">
            <div className="my-account-section__heading">
              <p>Sécurité</p>
              <h3 id="my-account-security-title">Protection du compte</h3>
            </div>
            <div className="my-account-security-actions">
              <button
                className="my-account-security-action"
                type="button"
                aria-expanded={emailChangeOpen}
                onClick={() => {
                  setEmailChangeOpen((open) => !open);
                  setPasswordChangeOpen(false);
                }}
              >
                <span className="my-account-security-action__copy">
                  <strong>Adresse e-mail</strong>
                  <small>Modifier l’adresse utilisée pour vous connecter à Tosho.</small>
                </span>
              </button>
              {emailChangeOpen ? <EmailChangePanel /> : null}
              <button
                className="my-account-security-action"
                type="button"
                aria-expanded={passwordChangeOpen}
                onClick={() => {
                  setPasswordChangeOpen((open) => !open);
                  setEmailChangeOpen(false);
                }}
              >
                <span className="my-account-security-action__copy">
                  <strong>Mot de passe</strong>
                  <small>Modifier votre mot de passe et fermer toutes vos sessions.</small>
                </span>
              </button>
              {passwordChangeOpen ? <PasswordChangePanel /> : null}
              <button
                className="my-account-security-action"
                type="button"
                onClick={() => setPasskeyManagementOpen(true)}
              >
                <span className="my-account-security-action__copy">
                  <strong>Passkeys</strong>
                  <small>Ajouter ou supprimer les passkeys associées à votre compte.</small>
                </span>
                <PrototypeIcon className="my-account-security-action__icon" name="shield-check" />
              </button>
            </div>
          </section>

          <section className="my-account-section" aria-labelledby="my-account-session-title">
            <div className="my-account-section__heading">
              <p>Connexions</p>
              <h3 id="my-account-session-title">Mes sessions actives</h3>
            </div>
            <MyCurrentSession />
          </section>

          <section className="my-account-section" aria-labelledby="my-account-journal-title">
            <div className="my-account-section__heading">
              <p>Historique</p>
              <h3 id="my-account-journal-title">Journal de sécurité</h3>
            </div>
            <PersonalSecurityJournal />
          </section>
        </div>
      </section>
    </ModalBackdrop>
  );
}
